/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM, ErrorBoundary } from "@adapptio/lib-components";
import React, { Suspense, useCallback, useMemo } from "react";
import { IView, TViewComponent } from "../../../view/IView";
import { IViewInstance } from "../../../view/ViewService";
import { ViewLoading } from "./ViewLoading";
import { getViewFetcher } from "./getViewFetcher";
import { ViewError } from "./ViewError";
import { ViewDisplay } from "./ViewDisplay";

interface IViewContentProps {
	viewInstance: IViewInstance;
	ViewComponent: TViewComponent<IView<unknown>>;
	isActive: boolean;
	onReload: (viewInstance: IViewInstance) => void;
}

export const ViewContent = React.memo<IViewContentProps>(
	({ viewInstance, ViewComponent, isActive, onReload }) => {
		const viewInfo = viewInstance.view.getViewInfo();

		const viewFetcher = useMemo(() => getViewFetcher(viewInstance), [ viewInstance.view ]);

		const doReload = useCallback(() => {
			onReload(viewInstance);
		}, [ viewInstance ]);

		if (viewInfo.renderWhenNotActive === false && !isActive) {
			return null;
		}

		const baseClass = BEM("edt-view", "item", {
			active: isActive
		});

		baseClass.add("m-view-name--" + viewInstance.name);

		return (
			<div className={baseClass.toClassName()}>
				<ErrorBoundary fallback={<ViewError onReload={doReload} />}>
					<Suspense fallback={<ViewLoading />}>
						<ViewDisplay ViewComponent={ViewComponent} viewFetcher={viewFetcher} />
					</Suspense>
				</ErrorBoundary>
			</div>
		);
	},
	(prevProps, nextProps) => {
		if (prevProps?.viewInstance?.view !== nextProps?.viewInstance?.view) {
			return false;
		}
	}
);

ViewContent.displayName = "ViewContent";
